<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useEvaluationMeasureStore} from "@/stores/Evaluation/EvaluationMeasure";
import {computed, onMounted, ref} from "vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
        type: String,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);

        const {mxRules} = useValidation();

        const {dialog, loading, overlay} = useSharedData();
        const {operations} = useSharedSelectData();

        const {getBack, isTwoTest} = useSharedMethod();

        const {$refs, $route} = useTools();

        const evaluationMeasureStore = useEvaluationMeasureStore();

        const evaluationMeasure = ref({
            calculable: false,
            formulas: [],
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/evaluationMeasures/${id}`).then((response) => {
                evaluationMeasure.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }


        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                buildOrder();
                evaluationMeasure.value.type = props.type;
                axios.post('/evaluationMeasures', evaluationMeasure.value).then((response) => {
                    evaluationMeasureStore.addEvaluationMeasure(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                buildOrder();
                evaluationMeasure.value.type = props.type;
                axios.put('/evaluationMeasures', evaluationMeasure.value).then((response) => {
                    evaluationMeasureStore.updateEvaluationMeasure(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/evaluationMeasures/${evaluationMeasure.value.id}`).then(() => {
                    evaluationMeasureStore.deleteEvaluationMeasure(evaluationMeasure.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function buildOrder() {
            evaluationMeasure.value.formulas.forEach((formula, index) => {
                formula.order = index;
            });
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            evaluationMeasureStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            evaluationMeasure,
            operations,
            save,
            update,
            remove,
            getBack,
            isTwoTest
        };
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="1000"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-container grid-list-md>
                    <v-form ref="form">
                        <w-fieldset :title="$t('common.general')">
                            <v-row>
                                <v-col cols="12">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="evaluationMeasure.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="type === 'EVALUATION_PERFORMANCE'">
                                    <w-label :label="$t('common.kpi')" required/>
                                    <w-text-field
                                        v-model="evaluationMeasure.keyPerformanceIndicator"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <v-radio-group
                                        v-model="evaluationMeasure.calculable"
                                        :label="$t('common.calculable')"
                                        row
                                    >
                                        <v-radio :value="true" :label="$t('common.yes')"/>
                                        <v-radio :value="false" :label="$t('common.no')"/>
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset
                            v-if="evaluationMeasure.calculable"
                            :title="$t('common.formula')"
                            show-add-action
                            @add="evaluationMeasure.formulas.push({})"
                        >
                            <ul>
                                <li>{{ $t('common.variable') }} 1: <strong>[#NOTE#]</strong></li>
                                <li>{{ $t('common.variable') }} 2: <strong>[#CIBLE#]</strong></li>
                            </ul>
                            <br/>
                            <v-row
                                v-for="(formula, index) in evaluationMeasure.formulas"
                                :key="index"
                                dense
                            >
                                <v-col cols="2">
                                    <w-enum-select
                                        v-model="formula.operator"
                                        :items="operations"
                                        :readonly="readonly"
                                        :label="$t('common.operation')"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <w-text-field
                                        v-model="formula.firstTest"
                                        :readonly="readonly"
                                        label="V1"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <w-text-field
                                        v-model="formula.secondTest"
                                        :readonly="readonly"
                                        :disabled="!isTwoTest(formula.operator)"
                                        label="V2"
                                        :rules="isTwoTest(formula.operator) ? [mxRules.required] : []"
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <w-text-field
                                        v-model="formula.result"
                                        :readonly="readonly"
                                        :label="$t('common.result')"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="1">
                                    <w-btn-icon
                                        icon="mdi-trash-can"
                                        color="red"
                                        @press="evaluationMeasure.formulas.splice(index, 1)"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('common.comment')">
                            <w-simple-text-area
                                v-model="evaluationMeasure.notes"
                            />
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>